/**
 * Description  :
 * Version      : 1.0.0
 * Author       : Luoye
 * Date         : 2022-08-04 11:38:56
 * LastEditors  : Luoye
 * FilePath     : /namibox_client_web/src/common/utils/appUtils.ts
 * Copyright (C) 2022 rose. All rights reserved.
 */

import { PAGE_HOST, host } from '../config';
import { getData, postData } from './request';
import { getShareSign, getShareSignValue } from '~/web-public.lib/core/utils';

/**
 * 获取当前浏览器环境和运行平台
 * @returns
 */
export function getBrowser() {
  const ua = window.navigator.userAgent.toLowerCase();
  let browser = 'other';
  let platform = '';
  let browserType = '';
  if (ua.indexOf('namibox') > -1) {
    browser = 'namibox';
  } else if (ua.indexOf('aiclass') > -1) {
    browser = 'aiclass';
  } else if (ua.indexOf('micromessenger') > -1) {
    browser = 'weixin';
    if (ua.indexOf('wxwork') > -1) {
      browserType = 'qiyeweixin';
    }
  } else if (ua.indexOf('nbdiyi') > -1) {
    browser = 'nbdiyi';
  }
  if (ua.indexOf('android') > -1) {
    platform = 'android';
  } else if (ua.indexOf('harmony') > -1) {
    platform = 'harmony';
  } else {
    platform = 'ios';
  }
  return { browser, platform, browserType };
}

/**
 * 将APP查询返回的版本号转为整数
 * @param {string} version
 * @return {number}
 * @example 如1.0转换为10000，1.0.2转换为10002
 */
export function convert_app_version(version: string): number {
  const _v = version.split('.');
  const count_append = 3 - _v.length;
  for (let index = 0; index < count_append; index++) {
    _v.push('0');
  }
  return (
    parseInt(_v[0], 10) * 10000 +
    parseInt(_v[1], 10) * 100 +
    parseInt(_v[2], 10)
  );
}

/**
 * 获取app版本
 * @returns
 */
export function getAppVersion(): number {
  const { browser } = getBrowser();
  if (browser === 'namibox' || browser === 'nbdiyi') {
    const _info = navigator.userAgent.match(/NamiBox\/(.*)\/([\d.]+).*/i);
    if (_info) {
      return convert_app_version(_info[2] || '0.0.0');
    }
  }
  return 0;
}

export function setAppTitle(title: string = '', color: string = '') {
  const { browser } = getBrowser();
  if (browser === 'namibox' || browser === 'aiclass') {
    window.top?.jssdk.appReady(() => {
      window.top?.jssdk.send({ command: 'changetitle', title, color });
    });
  } else {
    window.document.title = title;
  }
}

export function openAppMarket() {
  const { browser, platform } = getBrowser() || {};
  let marketUrl = 'market://details?id=com.jinxin.namibox';
  if (platform === 'ios') {
    marketUrl = 'https://apps.apple.com/cn/app/id913503440';
  } else {
    const ua = window.navigator.userAgent;
    const appMarketScheme: any = {
      HuaweiBrowser: 'appmarket://details?id=com.jinxin.namibox', // 华为  HuaweiBrowser
      VivoBrowser: 'vivoMarket://details?id=com.jinxin.namibox', // vivo  VivoBrowser
      MiuiBrowser: 'mimarket://details?id=com.jinxin.namibox', // 小米  XiaoMi/MiuiBrowser
      lenovo: 'http://market.lenovomm.com/details?id=com.jinxin.namibox', // 联想
      MZBrowser: 'mstore://details?package_name=com.jinxin.namibox', // 魅族  MZBrowser
      HeyTapBrowser: 'market://details?id=com.jinxin.namibox', //OPPO  HeyTapBrowser
    };
    Object.keys(appMarketScheme).forEach((name) => {
      const reg = new RegExp(name);
      if (reg.test(ua)) {
        marketUrl = appMarketScheme[name];
      }
    });
  }
  if (browser === 'namibox') {
    window.top?.jssdk.send({
      command: 'openurl',
      url: marketUrl,
    });
  } else {
    window.location.href = marketUrl;
  }
}

// 升级app方法
export function updateApp() {
  const ua = navigator.userAgent;
  const isAndroid = ua.indexOf('Android') > -1 || ua.indexOf('Adr') > -1;
  const isiOS = !!ua.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);

  let appMarket = 'market://details?id=com.jinxin.namibox';
  const appMarketScheme: any = {
    HuaweiBrowser: 'appmarket://details?id=com.jinxin.namibox', // 华为  HuaweiBrowser
    VivoBrowser: 'vivoMarket://details?id=com.jinxin.namibox', // vivo  VivoBrowser
    MiuiBrowser: 'mimarket://details?id=com.jinxin.namibox', // 小米  XiaoMi/MiuiBrowser
    lenovo: 'http://market.lenovomm.com/details?id=com.jinxin.namibox', // 联想
    MZBrowser: 'mstore://details?package_name=com.jinxin.namibox', // 魅族  MZBrowser
    HeyTapBrowser: 'market://details?id=com.jinxin.namibox', //OPPO  HeyTapBrowser
  };
  Object.keys(appMarketScheme).forEach((name) => {
    const reg = new RegExp(name);
    if (reg.test(ua)) {
      appMarket = appMarketScheme[name];
    }
  });

  if (isiOS) {
    window.location.href = `https://apps.apple.com/cn/app/id913503440`;
  } else if (isAndroid) {
    window.location.href = appMarket;
  }
}

export function openView(url: string, full = true, view_name = '') {
  const { browser } = getBrowser();
  url = /^https?:\/\//.test(url) ? url : `${PAGE_HOST}${url}`;
  if (browser === 'namibox') {
    window.top?.jssdk.send({
      command: 'openview',
      template: full ? 'fullscreen_notitle' : '',
      url,
      view_name,
    });
  } else {
    window.location.href = url;
  }
}

/**
 * 打开下载页地址
 * @param shareSource 分享渠道key
 * @returns
 */
export async function openInstallAppPageUrl(shareSource = '') {
  //如果地址栏包含share_sign，直接使用share_sign，不存在查share_source，传的share_source优先
  const shareSign = getQueryString('share_sign');
  shareSource = shareSource || getQueryString('share_source') || '其他';

  const { share_sign, share_point } = window.td.pagePropertys;
  let _data: any = {};

  if (shareSign === share_sign || shareSource === share_point) {
    _data = { share_sign, share_point };
  } else {
    if (shareSign) {
      _data = await getShareSignValue(shareSign);
    } else {
      _data = await getShareSign(shareSource);
    }
    //分享点刷新后再次上报
    window.td.setPageProperty(_data);
    window.td.track('AppH5Views', {
      page: document.title || '',
      repeatReportReason: 'refreshShareSign',
    });
  }

  const _shareSign = _data?.share_sign || '';
  const link_url = `${PAGE_HOST}/app-guide/index.html#/appstore?share_sign=${_shareSign}&_fromReferer=${encodeURIComponent(
    location.href
  )}`;
  const ret_url = `https://namibox.com/api/app/download_client?link_url=${encodeURIComponent(
    link_url
  )}`;
  const ret = { link: ret_url, shareSign: _shareSign };

  console.log('微信中拉起APP失败跳转连接：', ret);

  return Promise.resolve(ret);
}
/**
 * 上报渠道统计标记
 * @param shareSign 分享渠道key
 */
export function updateShareSign(shareSign = '') {
  if (shareSign) {
    postData(`${host}/qiyewx/on_open_share_link?share_sign=${shareSign}`);
  }
}
/**
 * 上报渠道统计海报id
 * @param posterId 海报id
 */
export function updateSharePosterId(posterId = '') {
  if (posterId) {
    getData(`${host}/qiyewx/on_send_share_link/?poster_id=${posterId}`);
  }
}

interface OpenUrlProps {
  url: string;
  region?: string;
  view_name?: string;
  newWindow?: boolean;
  close?: boolean;
}

export function openUrl(params: OpenUrlProps) {
  const {
    url = '',
    region,
    view_name,
    newWindow = true,
    close = true,
  } = params || {};
  const jumpUrl = url.startsWith('http') ? url : `${host}${url}`;
  const { browser } = getBrowser();
  if (browser === 'namibox' && newWindow) {
    window.top?.jssdk.appReady(() => {
      window.top?.jssdk.send({
        command: 'openview',
        region_name: region || 'popup',
        view_name: view_name || 'smart_win',
        url: jumpUrl,
        slide_direction: 'right',
      });
      if (close) {
        setTimeout(() => {
          window.top?.jssdk.send({ command: 'closeview' });
        }, 500);
      }
    });
  } else {
    window.location.href = jumpUrl;
  }
}

/**
 * 获取URL参数
 * @param name
 * @returns
 */
export function getQueryString(name: string) {
  const reg = new RegExp(`(^|&)${name}=([^&]*)(&|$)`);
  const r =
    window.location.search.substr(1).match(reg) ||
    window.location.hash
      .substring(window.location.hash.search(/\?/) + 1)
      .match(reg);
  if (r !== null) {
    return decodeURIComponent(r[2]);
  }
  return '';
}

// 生成唯一id
export function generateUuid() {
  let d = new Date().getTime();
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
    /[xy]/g,
    function (c) {
      const r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    }
  );
  return uuid.toUpperCase();
}
